import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLine, useAdminLoad } from '../aiAccess/shared';
import { useEnrichmentClient, type EnrichmentClient, type ProductLookup, type ProductStats } from './api';
import { SummaryTiles } from './SummaryTiles';
import { ProductsPanel } from './ProductsPanel';
import { UploadsTable } from './UploadsTable';
import { SerialDrawer } from './SerialDrawer';
import { UploadModal, type UploadMode } from './UploadModal';
import { Icon, buttonClass, chipClass } from './ui';

type View = 'products' | 'uploads';

/**
 * Enriched data: products and OEM warranties by serial number, from Service Data Enrichment
 * (part-enrichment-service) and stored in the tenant's SOR records. The summary tiles, then the
 * stored products (search, which also looks new serials up; warranty and OEM filters) or the
 * uploads; a serial opens in a drawer on the right, and "Upload a list" / "Import answers" open
 * the upload dialog, which keeps running minimised.
 */
export function EnrichedDataTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useEnrichmentClient(baseUrl);
  const stats = useAdminLoad<ProductStats, EnrichmentClient>(client, (c, signal) => c.stats(signal));
  // Bumped when a lookup, list run or import stored something, so counts and lists load again.
  const [version, setVersion] = useState(0);
  const changed = useCallback(() => setVersion((current) => current + 1), []);
  const [view, setView] = useState<View>('products');
  const [drawer, setDrawer] = useState<{ serial: string; answer: ProductLookup | null } | null>(null);
  const [upload, setUpload] = useState<{ mode: UploadMode } | null>(null);
  const [minimised, setMinimised] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);

  const reloadStatsRef = useRef(stats.reload);
  reloadStatsRef.current = stats.reload;
  const firstVersion = useRef(version);
  useEffect(() => {
    if (version !== firstVersion.current) void reloadStatsRef.current();
  }, [version]);

  const openUpload = (mode: UploadMode) => {
    // A list still running (minimised) opens again rather than a second one.
    if (upload) setMinimised(false);
    else setUpload({ mode });
  };

  const data = stats.data;
  const products = data?.units?.total ?? data?.records?.serial;
  const uploads = data?.records?.batch;
  const views: Array<{ key: View; label: string; count: number | undefined }> = [
    { key: 'products', label: t('Products'), count: products },
    { key: 'uploads', label: t('Uploads'), count: uploads },
  ];

  return (
    <div className="flex max-w-[1180px] flex-col px-6 pb-10 font-sans text-[#0B1B3D] dark:text-[#e6edf3]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex-[1_1_420px]">
          <h1 className="m-0 mb-1.5 text-2xl font-bold tracking-[-0.01em]">{t('Enriched data')}</h1>
          <p className="m-0 max-w-[640px] text-sm leading-normal text-[#5C6B85] dark:text-[#8b949e]">
            {t('Product and OEM warranty for your serial numbers. Each serial is checked on the OEM’s site once, then served from your stored records.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <button type="button" className={buttonClass()} onClick={() => openUpload('import')} disabled={!client}>
            <Icon name="file" />{t('Import answers')}
          </button>
          <button ref={uploadButtonRef} type="button" className={buttonClass({ primary: true })} onClick={() => openUpload('lookup')} disabled={!client}>
            <Icon name="upload" />{t('Upload a list')}
          </button>
        </div>
      </div>

      <ErrorLine message={stats.error} />
      {data && <div className="mb-6"><SummaryTiles stats={data} /></div>}

      <div role="tablist" aria-label={t('Enriched data views')} className="mb-3 flex gap-1.5">
        {views.map((entry) => (
          <button
            key={entry.key}
            id={`enriched-view-${entry.key}`}
            type="button"
            role="tab"
            aria-selected={view === entry.key}
            aria-controls={`enriched-panel-${entry.key}`}
            className={chipClass(view === entry.key)}
            onClick={() => setView(entry.key)}
          >
            {entry.label}
            {entry.count !== undefined && ' '}
            {entry.count !== undefined && <span className={view === entry.key ? 'opacity-80' : ''}>{entry.count.toLocaleString()}</span>}
          </button>
        ))}
      </div>

      {/* Both views stay mounted, so a lookup under way and the search survive switching. */}
      <div id="enriched-panel-products" role="tabpanel" aria-labelledby="enriched-view-products" hidden={view !== 'products'}>
        <ProductsPanel
          client={client}
          stats={data}
          version={version}
          onOpen={(serial, answer) => setDrawer({ serial, answer })}
          onStored={changed}
          searchRef={searchRef}
        />
        {data && (
          <p className="mx-0.5 mb-0 mt-3 text-xs text-[#5C6B85] dark:text-[#8b949e]">
            {t('Stored answers are re-checked with the OEM after {{days}} days; serials the OEM did not know, after {{retry}}.', {
              days: data.recheck_days,
              retry: data.not_found_retry_days,
            })}
          </p>
        )}
      </div>
      <div id="enriched-panel-uploads" role="tabpanel" aria-labelledby="enriched-view-uploads" hidden={view !== 'uploads'}>
        <UploadsTable client={client} version={version} active={view === 'uploads'} />
      </div>

      {drawer && (
        <SerialDrawer
          key={drawer.serial}
          client={client}
          serial={drawer.serial}
          answer={drawer.answer}
          onClose={() => setDrawer(null)}
          onStored={changed}
          fallbackFocus={searchRef}
        />
      )}
      {upload && (
        <UploadModal
          client={client}
          initialMode={upload.mode}
          minimised={minimised}
          onMinimise={() => setMinimised(true)}
          onRestore={() => setMinimised(false)}
          onClose={() => {
            setUpload(null);
            setMinimised(false);
          }}
          onChanged={changed}
          fallbackFocus={uploadButtonRef}
        />
      )}
    </div>
  );
}

export default EnrichedDataTab;
